import { RedoCheckbox } from "@redotech/arbiter/checkbox/redo-checkbox";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import {
  RedoModal,
  RedoModalButtonProps,
} from "@redotech/arbiter/modal/redo-modal";
import { Text } from "@redotech/arbiter/text";
import { useTranslation } from "@redotech/redo-i18n";
import { memo, useState } from "react";
import { getLocalizedViewNameValidationError } from "./view-name-validation";

/** The per-view settings the view editor offers next to the name. */
export interface SaveViewSettings {
  showNotification: boolean;
  hideWhenEmpty: boolean;
}

export const SaveTableViewModal = memo(function SaveTableViewModal({
  cancel,
  save,
  reservedViewNames,
  variant = "legacy",
  suggestedName = "",
  subtitle,
  settings: initialSettings,
  editing,
}: {
  save(name: string, settings?: SaveViewSettings): Promise<void>;
  cancel(): void;
  reservedViewNames: Set<string>;
  /** "prototype" is the new returns experience's design; "legacy" is unchanged for every other table. */
  variant?: "legacy" | "prototype";
  /** Prototype variant: pre-filled name, such as one built from the current filters. */
  suggestedName?: string;
  /** Prototype variant: where the view will show up. */
  subtitle?: string;
  /** Starting values for the notification settings; the prototype variant lets the user change them. */
  settings?: SaveViewSettings;
  /** Prototype variant: edit this existing view's name and settings instead of creating one. */
  editing?: { name: string };
}) {
  const { t } = useTranslation();
  const prototype = variant === "prototype";
  const [viewName, setViewName] = useState(
    prototype ? (editing?.name ?? suggestedName) : "",
  );
  const [settings, setSettings] = useState(initialSettings);
  // The legacy modal saves the name as typed, as it always has.
  const nameToSave = prototype ? viewName.trim() : viewName;

  const submitDisabledBecauseOfEmptyName =
    nameToSave.length === 0 ? t("You must name this view") : false;
  const submitDisabledBecauseOfInvalidName =
    getLocalizedViewNameValidationError(t, nameToSave) ?? false;
  const keepsOwnName =
    editing !== undefined &&
    nameToSave.toLowerCase() === editing.name.toLowerCase();
  const submitDisabledBecauseOfDuplicateName =
    !keepsOwnName && reservedViewNames.has(nameToSave.toLowerCase())
      ? t("A view with this name already exists")
      : false;

  const submitDisabled =
    submitDisabledBecauseOfEmptyName ||
    submitDisabledBecauseOfInvalidName ||
    submitDisabledBecauseOfDuplicateName;

  const submit = async () => {
    if (submitDisabled) {
      return;
    }
    await save(nameToSave, settings);
  };

  if (!prototype) {
    return (
      <RedoModal
        buttonPlacement="full"
        onModalCloseRequested={cancel}
        primaryButton={{
          text: t("Confirm"),
          onClick: submit,
          disabled: submitDisabled,
        }}
        secondaryButton={{ text: t("Cancel"), onClick: cancel }}
        title={t("Save as view")}
      >
        <Flex dir="column" gap="xl">
          <Flex dir="column">
            <Text>{t("Name")}</Text>
            <RedoTextInput setValue={setViewName} size="md" value={viewName} />
          </Flex>
          <div data-web="SaveTableViewModal" />
        </Flex>
      </RedoModal>
    );
  }

  const primaryButton: RedoModalButtonProps = {
    text: editing ? t("Save changes") : t("Save view"),
    onClick: submit,
    disabled: submitDisabled,
    size: "xs",
  };

  const secondaryButton: RedoModalButtonProps = {
    text: t("Cancel"),
    onClick: cancel,
    size: "xs",
  };

  return (
    <RedoModal
      modalSize="sm"
      onModalCloseRequested={cancel}
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
      subtitle={
        editing
          ? t("Changes the name and settings of this view, not its filters.")
          : (subtitle ?? t("Saves the current search and filters as a view."))
      }
      title={editing ? t("Edit view") : t("Create view")}
    >
      <Flex dir="column" gap="xl">
        <RedoTextInput
          autoFocus
          fullWidth
          label={t("View name")}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              void submit();
            }
          }}
          placeholder={t("Enter name…")}
          setValue={setViewName}
          size="md"
          value={viewName}
        />
        {settings && (
          <Flex dir="column" gap="md">
            <RedoCheckbox
              label={t("Enable notifications")}
              setValue={(value) =>
                setSettings({ ...settings, showNotification: value === true })
              }
              size="sm"
              value={settings.showNotification}
            />
            <RedoCheckbox
              label={t("Hide when empty")}
              setValue={(value) =>
                setSettings({ ...settings, hideWhenEmpty: value === true })
              }
              size="sm"
              value={settings.hideWhenEmpty}
            />
          </Flex>
        )}
      </Flex>
      <div data-web="SaveTableViewModal" />
    </RedoModal>
  );
});

/** After filters change on a saved view: update it, or save them as a new view. The caller does the saving. */
export const SaveViewChoiceModal = memo(function SaveViewChoiceModal({
  viewName,
  onUpdate,
  onSaveAsNew,
  cancel,
  pending,
}: {
  viewName: string;
  onUpdate(): void;
  onSaveAsNew(): void;
  cancel(): void;
  pending?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <RedoModal
      modalSize="sm"
      onModalCloseRequested={cancel}
      primaryButton={{
        text: t("Save current view"),
        onClick: onUpdate,
        pending,
        size: "xs",
      }}
      secondaryButton={{
        text: t("Save as new view"),
        onClick: onSaveAsNew,
        size: "xs",
      }}
      subtitle={t(
        "Update “{{viewName}}” with these filters, or keep it as it is and save them as a new view.",
        { viewName },
      )}
      title={t("Save changes")}
    />
  );
});
